import { components } from "@/api-schema";
import { PathsV1CompanySearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { isOperationalCustomerCompany } from "@/modules/masterdata/company/company-identifiers";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["CompanySearchBody"];

const FILTER_PAGE_SIZE = 50;

// Custom hook to fetch company data with pagination
function useCompanyFilterData(
  body: QueryBody,
  operationalCustomersOnly: boolean,
): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: companies,
    isPending: isLoading,
    error,
  } = useCompanySearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        extend: operationalCustomersOnly
          ? [PathsV1CompanySearchPostParametersQueryExtend.CompanyIdentifiers]
          : undefined,
      },
      body,
    },
  });

  const options =
    companies?.data
      .filter((company) => !operationalCustomersOnly || isOperationalCustomerCompany(company))
      .map((element) => ({
        id: element.uuid ?? "",
        name: element.name ?? "",
      }))
      .filter((option) => option.id && option.name)
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) || [];

  const loadedCount = companies?.data?.length ?? 0;
  const total = companies?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class CompanyFilter extends Filter<QueryBody, unknown> {
  readonly operationalCustomersOnly: boolean;
  readonly id: string = "companies";
  readonly labelSingular = "common:Company";
  readonly labelPlural = "common:Companies";
  readonly placeholder = "common:SelectCompanies";
  readonly searchPlaceholder = "common:SearchCompanies";
  readonly emptyText = "common:NoCompaniesFound";

  constructor({
    id,
    body = {},
    operationalCustomersOnly = false,
  }: { id?: string; body?: Partial<QueryBody>; operationalCustomersOnly?: boolean } = {}) {
    super();
    this.operationalCustomersOnly = operationalCustomersOnly;
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <CompanyFilterComponent filter={this} {...props} />;
  }
}

function CompanyFilterComponent({ filter, ...props }: { filter: CompanyFilter } & BaseFilterProps) {
  const { data, pagination } = useCompanyFilterData(filter.body, filter.operationalCustomersOnly);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
